import * as React from "react";
import {useMemo, useState} from "react";
import {IPlansTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {PLAN_FEATURE_DESCRIPTION} from "../shared/analytics.constants";
import {PlanDrilldownTable} from "./PlanDrilldownTable.component";
import {usePlanDrilldown} from "./usePlanDrilldown";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IPlanFeatureTableProps {
    rows: IPlansTabResponse["planFeatureUsage"];
    range: { startDate: string; endDate: string };
    country: string;
}

const cellClass = (value: number): string =>
    value > 0 ? "admin-analytics__cell--active" : "admin-analytics__cell--zero";

const displayValue = (value: number): number | string => value || "—";

export const PlanFeatureTable: React.FC<IPlanFeatureTableProps> = ({rows, range, country}) => {
    const [planFeatureSort, setPlanFeatureSort] = useState<{ col: string; dir: SortDir }>({
        col: "totalVisits",
        dir: "desc",
    });
    const drilldown = usePlanDrilldown(range, country);

    const sortedPlanFeature = useMemo(
        () => applySort(rows, planFeatureSort.col, planFeatureSort.dir),
        [rows, planFeatureSort]);

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Plan Feature Usage</h2>
                <span className="admin-analytics__section-subtitle">Which site features are plans using?</span>
            </div>
            <p className="admin-analytics__section-desc">{PLAN_FEATURE_DESCRIPTION}</p>
            <div className="admin-analytics__table-wrapper">
                <table
                    className="admin-analytics__table admin-analytics__table--matrix admin-analytics__table--clickable">
                    <thead>
                    <tr>
                        {sortTh("planName", "Plan", planFeatureSort, setPlanFeatureSort, undefined, "asc")}
                        {sortTh("research", "Research", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("clientReporting", "Client Reporting", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("sharedDocs", "Shared Docs", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("portfolio", "Portfolio", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("whitePapers", "White Papers", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("marketOutlooks", "Market Outlooks", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                        {sortTh("totalVisits", "Total", planFeatureSort, setPlanFeatureSort,
                            "admin-analytics__cell--right")}
                    </tr>
                    </thead>
                    <tbody>{sortedPlanFeature.map((row) => (
                        <React.Fragment key={row.planName}>
                            <tr
                                className={`admin-analytics__row--expandable${
                                    drilldown.expanded === row.planName ? " admin-analytics__row--expanded" : ""}`}
                                onClick={() => drilldown.toggle(row.planName)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") drilldown.toggle(row.planName);
                                }}
                            >
                                <td>
                                    <i className={`far ${
                                        drilldown.expanded === row.planName ? "fa-chevron-down" : "fa-chevron-right"
                                    } admin-analytics__expand-icon`}
                                       aria-hidden="true"/>
                                    {row.planName}
                                </td>
                                <td className={cellClass(row.research)}>{displayValue(row.research)}</td>
                                <td className={cellClass(row.clientReporting)}>
                                    {displayValue(row.clientReporting)}
                                </td>
                                <td className={cellClass(row.sharedDocs)}>{displayValue(row.sharedDocs)}</td>
                                <td className={cellClass(row.portfolio)}>{displayValue(row.portfolio)}</td>
                                <td className={cellClass(row.whitePapers)}>{displayValue(row.whitePapers)}</td>
                                <td className={cellClass(row.marketOutlooks)}>
                                    {displayValue(row.marketOutlooks)}
                                </td>
                                <td><strong>{numberWithCommas(row.totalVisits)}</strong></td>
                            </tr>
                            {drilldown.expanded === row.planName && (
                                <tr className="admin-analytics__drilldown-row">
                                    <td colSpan={8}>
                                        <PlanDrilldownTable
                                            data={drilldown.data}
                                            loading={drilldown.loading}
                                            colSpan={8}
                                            showUserColumns
                                            loadingMessage="Loading feature details..."
                                        />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}</tbody>
                </table>
            </div>
        </section>
    );
};
